import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Minus, Plus, Truck, ShieldCheck, Sparkles, ArrowLeft, Loader2, Check, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { resolveImage, formatPrice, whatsappLink, phoneLink } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { ReviewsSection } from "@/components/ReviewsSection";
import { StarRating } from "@/components/StarRating";

const WhatsappIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-heading text-2xl font-bold">Produs inexistent</h1>
        <Button asChild className="mt-6 rounded-full"><Link to="/magazin">Înapoi la magazin</Link></Button>
      </div>
    );

  if (!product)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );

  const discount = product.old_price > product.price
    ? Math.round((1 - product.price / product.old_price) * 100) : 0;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="product-detail-page">
      <button onClick={() => navigate(-1)} className="mb-6 flex items-center gap-1 text-sm text-slate-500 hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> Înapoi
      </button>

      <div className="grid gap-10 lg:grid-cols-2">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
          {discount > 0 && (
            <Badge className="absolute left-4 top-4 z-10 animate-pulse bg-accent text-white shadow-lg shadow-accent/40 hover:bg-accent" data-testid="detail-offer-badge">-{discount}% OFERTĂ</Badge>
          )}
          <img
            src={resolveImage(product.images?.[0])}
            alt={product.name}
            className="aspect-square w-full object-contain p-8 transition-transform duration-500 hover:scale-105"
          />
        </div>

        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">{product.category}</span>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            {product.name}
          </h1>

          {product.rating_count > 0 && (
            <a href="#reviews" className="mt-2 flex items-center gap-2" data-testid="detail-rating-summary">
              <StarRating value={product.rating_avg} size={16} />
              <span className="text-sm font-medium text-slate-700">{product.rating_avg}</span>
              <span className="text-sm text-slate-400 underline-offset-2 hover:underline">({product.rating_count} recenzii)</span>
            </a>
          )}

          <div className="mt-4 flex items-end gap-3">
            <span className="font-heading text-4xl font-bold text-slate-900">{formatPrice(product.price)}</span>
            {product.old_price > product.price && (
              <span className="mb-1 text-lg text-slate-400 line-through">{formatPrice(product.old_price)}</span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {product.volume && <Badge variant="secondary">{product.volume}</Badge>}
            {product.scent && <Badge variant="secondary">{product.scent}</Badge>}
            {product.washes && <Badge variant="secondary">{product.washes}{product.category === "Pachete promo" ? "+" : ""} spălări</Badge>}
          </div>

          <p className="mt-6 leading-relaxed text-slate-600">{product.description}</p>

          <Separator className="my-6" />

          <div className="flex items-center gap-4">
            <div className="flex items-center rounded-full border border-slate-300">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 text-slate-600 hover:text-primary" data-testid="qty-minus">
                <Minus className="h-4 w-4" />
              </button>
              <span className="w-8 text-center font-medium" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="p-3 text-slate-600 hover:text-primary" data-testid="qty-plus">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <Button
              size="lg"
              className="flex-1 rounded-full hover:bg-primary/90"
              onClick={() => { addItem(product, qty); navigate("/finalizare"); }}
              data-testid="detail-buy-now"
            >
              <ShoppingCart className="mr-2 h-5 w-5" /> Comandă acum
            </Button>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <Button asChild size="lg" className="rounded-full bg-[#25D366] text-white hover:bg-[#1ebe57]" data-testid="detail-whatsapp">
              <a href={whatsappLink(product)} target="_blank" rel="noopener noreferrer">
                <WhatsappIcon className="mr-2 h-5 w-5" /> Comandă pe WhatsApp
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-primary text-primary hover:bg-primary hover:text-white" data-testid="detail-phone">
              <a href={phoneLink()}>
                <Phone className="mr-2 h-5 w-5" /> Comandă telefonic
              </a>
            </Button>
          </div>

          <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-success" data-testid="detail-free-shipping">
            <Truck className="h-4 w-4" /> Transport GRATUIT la toate comenzile
          </p>

          {product.benefits?.length > 0 && (
            <div className="mt-8">
              <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">Beneficii</h3>
              <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="product-benefits">
                {product.benefits.map((b) => (
                  <p key={b} className="flex items-center gap-2 text-sm text-slate-700">
                    <Check className="h-4 w-4 shrink-0 text-success" /> {b}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm">
            <p className="flex items-center gap-2 text-slate-700"><Truck className="h-4 w-4 text-success" /> Transport gratuit la toate comenzile</p>
            <p className="flex items-center gap-2 text-slate-700"><ShieldCheck className="h-4 w-4 text-primary" /> Curățenie garantată</p>
            <p className="flex items-center gap-2 text-slate-700"><Check className="h-4 w-4 text-primary" /> Plată ramburs la livrare</p>
            <p className="flex items-center gap-2 text-slate-700"><Sparkles className="h-4 w-4 text-primary" /> Parfum de lungă durată</p>
          </div>
        </div>
      </div>

      <div id="reviews">
        <ReviewsSection productId={product.id} />
      </div>
    </div>
  );
}
